/* eslint-disable no-nested-ternary */
import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import Skeleton from '@mui/material/Skeleton';

import History from '@mui/icons-material/History';
import { EventService } from '@pingum/api-services';

import {
  ListItemButton,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import {
  AccessAlarm,
  AccessTime,
  CheckBox,
  CheckBoxOutlineBlank,
  InfoOutlined,
  LocationOn,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getBarnHorse } from '../../../../../../components/Schedule/ScheduleTaskItem';
import Horse from '../../../../../../components/Horse';
import { primary } from '../../../../../../utils/brandColors';
import TaskStateLabel from '../../../../../../components/TaskStateLabel';
import { getRelativeTime } from '../../../../../../utils/dateHelper';

const TaskHistory = ({
  eventTemplate,
  tag,
  anchorEl,
  onClose,
  inline,
  handleTaskSelect,
  tasks: propTasks,
  barn,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [tasks, setTasks] = React.useState(false);

  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const reduxBarn = useSelector((state) => state.barn.selectedBarn);
  const selectedBarn = barn || reduxBarn; // Public page wont' have this

  React.useEffect(() => {
    if (propTasks) {
      setTasks(propTasks)
    } else if (eventTemplate && eventTemplate.id && tag) {
      setIsLoading(true);
      // get recent tasks and display
      EventService.searchEvents({
        eventTemplateId: eventTemplate.id,
        includeComplete: true,
        includeArchived: false,
        tagConditions: [[tag]],
        presetDateFilter: null,
      }, 0, 20, true).then((data) => {
        setIsLoading(false);
        setTasks(data.content);
      }).catch((error) => {
        console.error(error);
        setIsLoading(false);
        setTasks([]);
      })
    } else {
      setIsLoading(false);
      setTasks([]);
    }
  }, [eventTemplate, tag, propTasks]);

  const getTasksComponent = () => (
    <Box>
      {
        !inline && (
          <ListItem dense divider>
            <ListItemIcon><History /></ListItemIcon>
            <ListItemText
              primary={`Task History for ${(eventTemplate && eventTemplate.name) || ''}`}
            />
          </ListItem>
        )
      }
      {
        isLoading && (
          <Box>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
            <ListItem dense divider><Skeleton height={60} width="100%" sx={{ borderRadius: 2 }} /></ListItem>
          </Box>
        )
      }
      {
        (!isLoading && tasks.length > 0) && (
          <List
            sx={{
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            {tasks.map((task) => {
              const horseTagTypeId = (selectedBarn.barnMetadata && selectedBarn.barnMetadata.horseTagTypeId) || 0;
              const barnHorse = getBarnHorse(task, null, barnHorsesMap, horseTagTypeId);
              const priorDayTask = task && new Date(task.startDate) < moment().startOf('day').toDate().getTime();

              const color = (task.eventData && task.eventData.Color && task.eventData.Color.value) || primary[500]

              return (
                <ListItemButton
                  key={`task-history-${task.id}`}
                  alignItems="center"
                  sx={{ borderLeft: `8px solid ${color}` }}
                  divider
                  onClick={() => handleTaskSelect(task)}
                  dense
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    {
                      barnHorse
                        ? (
                          <Horse
                            horse={barnHorse.horse}
                            hideName
                            size={30}
                            clickable
                          />
                        )
                        : (
                          <Avatar
                            aria-label="recipe"
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: '10pt',
                            }}
                          >
                            <LocationOn />
                          </Avatar>
                        )
                    }
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={(
                      <Typography variant="body1" sx={{ lineHeight: 1 }} noWrap>
                        {task.name}
                      </Typography>
                    )}
                    secondary={(
                      <Box
                        style={{
                          display: 'flex',
                          marginTop: '0.25rem',
                          alignItems: 'center',
                        }}
                      >
                        <TaskStateLabel
                          archived={task && !!task.archived}
                          completed={task && !!task.completeDate}
                          // eslint-disable-next-line no-nested-ternary
                          dueDate={task
                            ? task.dueDate
                            : null}
                          priorDayTask={priorDayTask}
                          createdDate={task && task.startDate}
                          isTaskScheduled={false}
                          isTaskCreated
                          hideComplete
                        />
                        {
                          (task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime())
                            ? (
                              <AccessAlarm
                                sx={{
                                  fontSize: 18,
                                  marginRight: 0.5,
                                }}
                                color="secondary"
                              />
                            )
                            : (
                              <AccessTime
                                sx={{
                                  fontSize: 18,
                                  marginRight: 0.5,
                                }}
                                color="secondary"
                              />
                            )
                        }

                        <Typography
                          sx={{ display: 'inline' }}
                          component="span"
                          variant="caption"
                          color="text.primary"
                          noWrap
                        >
                          {task.completeDate
                            ? (`completed ${moment(task.completeDate).fromNow()}`)
                            : (task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime())
                              ? `due ${getRelativeTime(new Date(), task.dueDate)} ago`
                              : `${getRelativeTime(new Date(), task.startDate)} old`}

                        </Typography>
                      </Box>
                    )}
                  />
                  <ListItemSecondaryAction sx={{ right: 6 }}>
                    {
                      task.completeDate
                        ? (
                          <CheckBox
                            sx={{ color }}
                          />
                        )
                        : (
                          <CheckBoxOutlineBlank
                            sx={{ color }}
                          />
                        )
                    }
                  </ListItemSecondaryAction>
                </ListItemButton>
              )
            })}
          </List>
        )
      }
      {
        (!isLoading && tasks.length === 0) && (
          <Box
            sx={{
              textAlign: 'center',
              m: 4,
            }}
          >
            <InfoOutlined
              sx={{ fontSize: 50 }}
              color="secondary"
            />
            <Typography
              variant="subtitle1"
              align="center"
              component="div"
              sx={{
                my: 2,
                fontWeight: 300,
              }}
            >
              No Tasks
            </Typography>
          </Box>
        )
      }
    </Box>
  )

  if (inline) {
    return (
      getTasksComponent()
    )
  }
  return (
    <Popover
      id="task-history-quick-view"
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      sx={{
        minWidth: '400px !important',
        p: 2,
      }}
    >
      {getTasksComponent()}
    </Popover>
  );
};

TaskHistory.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  eventTemplate: PropTypes.object,
  tag: PropTypes.number,
  inline: PropTypes.bool,
  handleTaskSelect: PropTypes.func,
  tasks: PropTypes.array,
  barn: PropTypes.object,
};

TaskHistory.defaultProps = {
  anchorEl: null,
  eventTemplate: null,
  tag: null,
  inline: false,
  handleTaskSelect: () => { },
  onClose: () => { },
  tasks: null,
  barn: null,
};

export default TaskHistory;
